import { Tab, Tabs } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useLocation, useNavigate } from "react-router";
import {
  SETTINGS,
  FUNNEL_PAGES,
  NESTED_SIDENAV,
  CUSTOMER_ROUTES,
  EVALUATION_TYPE,
  MISSING_INFO_TYPE,
  CREATIVE_DASHBOARD_TABS,
  LEADSERVICE_PANEL_TABS,
  TOOLS_TYPE,
  SPLIT_TEST_TABS,
  SITE_SETTINGS_TYPE,
  TEMPLATES,
  WEBSITE_BASE_TAB_ROUTE,
  CUSTOMER_WEBSITE_ROUTE
} from "../strings";
import styled from "styled-components";
import { hasAccess } from "../../services/access";
import AddNewNestedTabButton from "./add-new-nested-tab-button";

const navigations = {
  [NESTED_SIDENAV.FUNNEL]: {
    [FUNNEL_PAGES.FUNNEL_FORM]: "form",
    [FUNNEL_PAGES.LANDING_PAGE]: "landing-page",
    [FUNNEL_PAGES.INTEGRATION_FORM]: "integration",
    [FUNNEL_PAGES.FUNNEL_INSIGHT]: "insight",
    [FUNNEL_PAGES.FUNNEL_FILTERS]: "filters",
    [FUNNEL_PAGES.AD_COPY]: "ad-copy",
    [FUNNEL_PAGES.LS_OVERVIEW]: "leadservice-overview",
    [FUNNEL_PAGES.CAMPAIGNS_REVIEW]: "campaigns-review",
    [FUNNEL_PAGES.PERFORMANCE]: "performance"
    // [FUNNEL_PAGES.AD_DUPLICATOR]: "ad-duplicator"
  },
  [NESTED_SIDENAV.TEMPLATE]: {
    [TEMPLATES.FUNNEL_TEMPLATE]: "funnel-templates",
    [TEMPLATES.GLOBAL_THANK_YOU_TEMPLATE]: "global-thank-you-template",
    [TEMPLATES.PRIVACY_CONSENT]: "privacy-consent"
  },
  [NESTED_SIDENAV.CUSTOMER]: {
    [CUSTOMER_ROUTES.OVERVIEW]: "overview",
    [CUSTOMER_ROUTES.FUNNELS]: "funnels",
    [CUSTOMER_ROUTES.LEADS]: "leads",
    [CUSTOMER_ROUTES.PERFORMANCE_REPORT]: "performance",
    [CUSTOMER_ROUTES.INVOICES]: "invoices",
    [CUSTOMER_ROUTES.WEBSITE]: CUSTOMER_WEBSITE_ROUTE
  },
  [NESTED_SIDENAV.SETTING]: {
    [SETTINGS.PROFILE]: "profile",
    [SETTINGS.SECURITY]: "security",
    [SETTINGS.CHANGE_PASSWORD]: "change-password"
  },
  [NESTED_SIDENAV.EVALUATION]: {
    [EVALUATION_TYPE.OVERVIEW]: "overview?dateRange=L14D",
    [EVALUATION_TYPE.INSIGHTS]: "insights",
    [EVALUATION_TYPE.OPTIMIZATION]: "optimization",
    [EVALUATION_TYPE.ZIP_CHECK]: "zipcheck"
    // [EVALUATION_TYPE.EXPORT_QUERIES]: "export-queries"
  },
  [NESTED_SIDENAV.ADS_EVALUATION]: {
    [CREATIVE_DASHBOARD_TABS.OVERVIEW]: "overview",
    [CREATIVE_DASHBOARD_TABS.SUGGESTION_GALLERY]: "suggestion-gallery",
    [CREATIVE_DASHBOARD_TABS.FUNNEL_OVERVIEW]:
      "funnel-overview?dateRange=BT_L14D"
  },
  [NESTED_SIDENAV.MISSING_INFO]: {
    [MISSING_INFO_TYPE.LEAD_INFO]: "lead",
    [MISSING_INFO_TYPE.CATEGORY_INFO]: "category",
    [MISSING_INFO_TYPE.CUSTOMER_INFO]: "customer",
    [MISSING_INFO_TYPE.HUBSPOT_INFO]: "hubspot",
    [MISSING_INFO_TYPE.AD_ACCOUNT]: "ad-account",
    [MISSING_INFO_TYPE.UTM_PARAMETER]: "utm-parameter"
  },
  [NESTED_SIDENAV.LEADSERVICE_PANEL]: {
    [LEADSERVICE_PANEL_TABS.LEADSERVICE]: "leadservice",
    [LEADSERVICE_PANEL_TABS.APPOINTMENTS_PANEL]: "lead-appointments"
  },
  [NESTED_SIDENAV.TOOLS]: {
    [TOOLS_TYPE.DUPLICATOR]: "fb-duplicator",
    [TOOLS_TYPE.WEBSITE_CREATION_LOGS]: "website-creation-logs"
  },
  [NESTED_SIDENAV.SITE_SETTINGS]: {
    [SITE_SETTINGS_TYPE.FACEBOOK_ACCOUNTS]: "facebook-accounts",
    [SITE_SETTINGS_TYPE.FORM_DEFAULTS]: "form-defaults",
    [SITE_SETTINGS_TYPE.COUNTRIES]: "countries"
  },
  [NESTED_SIDENAV.SPLIT_TESTS]: {
    [SPLIT_TEST_TABS.LEAD]: "lead",
    [SPLIT_TEST_TABS.EMPLOYEE]: "employee"
  },
  [NESTED_SIDENAV.WEBSITE]: {} // there will be dynamic sub tabs
};

const BubbleSpan = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 5px;
  background: #fd7e14;
  margin-left: 4px;
  margin-top: -8px;
`;

const NestedTabsComponent = ({
  activeTab = null,
  nestedNavType,
  tabs,
  clearParams
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const filteredTabs = tabs.filter(({ access }) => hasAccess(access));

  const pathParts = location.pathname.split("/").filter(Boolean);
  const parentTab = pathParts[3];
  const childTab = pathParts[4];
  // === Determine active tab ===
  let activeTabName;
  if (nestedNavType === NESTED_SIDENAV.WEBSITE) {
    const currentPath = `/${childTab || ""}`; // normalize
    // Find matching tab in tabs prop
    const matchedTab = tabs.find((tab) => tab.evKey === currentPath);
    activeTabName = matchedTab ? matchedTab.evKey : tabs[0]?.evKey || "/";
  } else if (nestedNavType === NESTED_SIDENAV.CUSTOMER) {
    activeTabName = parentTab;
  } else {
    activeTabName = activeTab || pathParts.pop();
  }

  // === Helper for non-dynamic tabs ===
  const getKeyByValue = (_object, activeVal) => {
    if (!activeVal) return null;
    return Object.keys(_object).find((key) => {
      const val = _object[key];
      if (!val) return false;
      return val.includes("?")
        ? val.split("?")[0] === activeVal
        : val === activeVal;
    });
  };

  const handleOnSelect = (evKey) => {
    if (clearParams) {
      for (const [key] of searchParams.entries()) {
        searchParams.delete(key);
      }
    }
    let path =
      nestedNavType === NESTED_SIDENAV.WEBSITE
        ? evKey
        : navigations[nestedNavType][evKey];

    if (nestedNavType === NESTED_SIDENAV.WEBSITE) {
      const basePath = `/${pathParts.slice(0, 4).join("/")}`;
      path =
        evKey === WEBSITE_BASE_TAB_ROUTE ? basePath : `${basePath}${evKey}`;
    }
    navigate({
      pathname: path,
      search: searchParams.toString()
    });
  };

  return (
    <Tabs
      fill="true"
      variant="pills"
      className="tab-container"
      onSelect={(evKey) => handleOnSelect(evKey)}
      activeKey={
        nestedNavType === NESTED_SIDENAV.WEBSITE
          ? activeTabName
          : getKeyByValue(navigations[nestedNavType], activeTabName)
      }
    >
      {filteredTabs.map((tab, tabIdx) => (
        <Tab
          key={tabIdx}
          title={
            <div
              className={
                tab.hasDynamicNestedTabs
                  ? "d-flex flex-fill align-items-center justify-content-between"
                  : ""
              }
            >
              {tab.title}
              {tab.popUp && <BubbleSpan />}
              {tab.hasDynamicNestedTabs && <AddNewNestedTabButton />}
            </div>
          }
          eventKey={tab.evKey}
          tabClassName="tab-item"
          disabled={tab.isDisabled}
        />
      ))}
    </Tabs>
  );
};
export default NestedTabsComponent;
